import {
  BottomSheet,
  Button,
  Checkbox,
  Chip,
  HSpacer,
  MenuItem,
  SegmentedButton,
  Text,
  TextLink,
} from '@/components/DesignSystem';
import { FilterOption } from '@/components/DesignSystem/Toolbar/interfaces';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import Check from '@mui/icons-material/Check';
import { Box, Menu, Stack, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PurchaseCategoryFilterChipProps {
  hasPurchasedCategory?: boolean,
  onChange: (categoryIds: string[], hasPurchased?: boolean) => void,
  options: FilterOption[],
  purchaseCategoryIds: string[],
  testID: string,
}

export const PurchaseCategoryFilterChip = ({
  hasPurchasedCategory,
  onChange,
  options,
  purchaseCategoryIds,
  testID,
}: PurchaseCategoryFilterChipProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isMobile } = useMediaQuery();

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const [pendingCategoryIds, setPendingCategoryIds] = useState<string[]>([]);
  const [pendingHasPurchased, setPendingHasPurchased] = useState<boolean>(true);

  const isActive = !!purchaseCategoryIds.length;

  const openPopover = (event: React.MouseEvent<HTMLDivElement>) => {
    setPendingCategoryIds([...purchaseCategoryIds]);
    setPendingHasPurchased(hasPurchasedCategory ?? true);
    if (isMobile) {
      setShowBottomSheet(true);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const closePopover = () => {
    setAnchorEl(null);
    setShowBottomSheet(false);
  };

  const handleApply = () => {
    onChange(pendingCategoryIds, pendingHasPurchased);
    closePopover();
  };

  const handleClear = () => {
    onChange([], undefined);
    closePopover();
  };

  const toggleCategory = (id: string) => {
    setPendingCategoryIds((prev) => (
      prev.includes(id) ? prev.filter((categoryId) => categoryId !== id) : [...prev, id]
    ));
  };

  const toggleMode = (hasPurchased: boolean) => {
    if (pendingHasPurchased !== hasPurchased) {
      setPendingCategoryIds([]);
    }
    setPendingHasPurchased(hasPurchased);
  };

  const filterLabel = t(($) => $.overview.filters.purchaseCategory);

  let chipLabel = filterLabel;
  if (isActive) {
    const firstOption = options.find((option) => option.id === purchaseCategoryIds[0]);
    chipLabel = firstOption?.label ?? filterLabel;
    if (purchaseCategoryIds.length > 1) {
      chipLabel += ` +${purchaseCategoryIds.length - 1}`;
    }
  }

  const isPendingEmpty = pendingCategoryIds.length === 0;

  const menuContent = (
    <Stack>
      <Box p="16px 16px 8px">
        <SegmentedButton
          color="primary"
          fullWidth
          selectedIndex={pendingHasPurchased ? 0 : 1}
          sx={{
            '& .MuiButtonBase-root:hover': { backgroundColor: 'inherit' },
            '& .MuiButtonGroup-firstButton': { borderRadius: '8px 0 0 8px' },
            '& .MuiButtonGroup-lastButton': { borderRadius: '0 8px 8px 0' },
          }}
        >
          <Button
            onClick={() => toggleMode(true)}
            testID={`${testID}-has-purchased-button`}
          >
            {t(($) => $.overview.filters.purchaseCategoryStatusOptions.hasPurchased)}
          </Button>
          <Button
            onClick={() => toggleMode(false)}
            testID={`${testID}-has-not-purchased-button`}
          >
            {t(($) => $.overview.filters.purchaseCategoryStatusOptions.hasNotPurchased)}
          </Button>
        </SegmentedButton>
      </Box>
      <Box maxHeight="300px" overflow="auto">
        {options.map((option) => (
          <MenuItem
            key={option.id}
            onClick={() => toggleCategory(option.id)}
            sx={{ padding: '8px 16px', width: '100%' }}
            testID={`${testID}-option-${option.id}`}
            value={option.id}
          >
            <Checkbox
              checked={pendingCategoryIds.includes(option.id)}
              testID={`${testID}-option-${option.id}-checkbox`}
            >
              {option.label}
            </Checkbox>
          </MenuItem>
        ))}
      </Box>
      <Box alignItems="center" display="flex" justifyContent="space-between" p="12px 16px">
        <TextLink
          category="label-medium"
          disabled={isPendingEmpty}
          onClick={handleClear}
          testID={`${testID}-clear-button`}
        >
          {t(($) => $.common.actions.clear)}
        </TextLink>
        <Stack direction="row" gap={1}>
          <Button
            onClick={closePopover}
            square
            sx={{ borderRadius: '8px', padding: '3px 15px' }}
            testID={`${testID}-cancel-button`}
            variant="outlined"
          >
            {t(($) => $.common.actions.cancel)}
          </Button>
          <Button
            onClick={handleApply}
            square
            sx={{ borderRadius: '8px', padding: '3px 15px' }}
            testID={`${testID}-apply-button`}
            variant="contained"
          >
            {t(($) => $.common.actions.apply)}
          </Button>
        </Stack>
      </Box>
    </Stack>
  );

  return (
    <>
      <Chip
        label={
          <Stack alignItems="center" direction="row">
            {isActive && (
              <>
                <Check sx={{ width: '18px' }} />
                <HSpacer size="3" />
              </>
            )}
            <Text category="label-large" testID={`${testID}-label`}>
              {chipLabel}
            </Text>
            <HSpacer size="3" />
            <ArrowDropDown sx={{ height: '18px', marginRight: '-4px', width: '18px' }} />
          </Stack>
        }
        onClick={openPopover}
        size="medium"
        sx={{
          '&:hover': {
            backgroundColor: isActive ? theme.palette.primaryContainer : undefined,
          },
          backgroundColor: isActive ? theme.palette.primaryContainer : undefined,
          borderColor: '#bdbdbd',
        }}
        testID={testID}
        variant={isActive ? 'filled' : 'outlined'}
      />
      {!!anchorEl && (
        <Menu
          PaperProps={{
            style: {
              borderRadius: '12px',
              marginTop: '12px',
              minWidth: '320px',
              padding: '4px 0',
            },
          }}
          anchorEl={anchorEl}
          data-testid={`${testID}-menu`}
          onClose={closePopover}
          open
        >
          {menuContent}
        </Menu>
      )}
      <BottomSheet
        onClose={closePopover}
        onOpen={() => undefined}
        open={showBottomSheet}
        showPuller
        testID={`${testID}-bottom-sheet`}
        title={filterLabel}
      >
        {menuContent}
      </BottomSheet>
    </>
  );
};
